import '../../../shared/StyledInput.css';
import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useErrorToast, useDismissError } from '../../../shared/ErrorToastContext';
import RequiredMark from '../../../shared/RequiredMark';
import { AvailabilityPill } from '../../../shared/Pill';
import ReportOverlay from '../../../shared/ReportOverlay';
import { reportButtonStyle } from '../../../shared/ReportShell';
import PriceLookupReportView from './PriceLookupReportView';
import {
  productLabel, customerLabel, groupLabel,
  SkipNotes, PriceLookupResultRow, RetailPriceMissingBanner,
} from './priceLookupShared';
import { todayInTenant, formatDate } from '../../../shared/tenantClock';
import DateInput from '../../../shared/DateInput';
import NumberInput from '../../../shared/NumberInput';
import { formatNumber } from '../../../shared/numberFormat';
import { SegmentedToggle } from '../../../shared/DashboardChart';
import { GST_VIEWS } from '../../../shared/gstView';

// The viewer's last Ex GST / Inc GST pick, for a lookup whose scope has no
// Tax Handling to open on. A per-viewer convenience only, so a blocked or
// empty store just means Ex GST.
const GST_VIEW_STORAGE_KEY = 'priceLookup.gstView';

function readStoredGstView() {
  try {
    return window.localStorage.getItem(GST_VIEW_STORAGE_KEY) === 'inc' ? 'inc' : 'ex';
  } catch {
    return 'ex';
  }
}

function storeGstView(view) {
  try {
    window.localStorage.setItem(GST_VIEW_STORAGE_KEY, view);
  } catch {
    // Not stored — the next lookup opens on Ex GST instead.
  }
}

// Price Lookup — resolves the actual price (Retail Price + winning
// Price, tie-broken) for one Product + UoM + Qty combo against
// either a specific Customer or a specific Customer Price Group (a direct,
// literal group test, not membership inference — Customers have no group-
// membership field, so this is the only way to see what a group would
// pay). UoM and Qty are both required, not just Product — a qty figure
// only means something against one specific UoM's own break schedule (5
// Boxes and 5 Packs are not the same order), so this is a true order-line
// simulation, not a browse-every-UoM report. Wraps
// backend/services/priceResolver.js's resolvePrice, which reuses
// priceResolution.js's scoreCandidate/pickWinner/
// applyPriceAtQuantity — the same winner-picking logic Pricelist 2.0
// already uses — rather than a second copy.

function PriceLookupPage() {
  const showError = useErrorToast();
  const dismissError = useDismissError();

  const [productId, setProductId] = useState('');
  const [productSearch, setProductSearch] = useState('');
  const [productResults, setProductResults] = useState([]);
  const [showProductResults, setShowProductResults] = useState(false);
  const skipNextProductSearch = useRef(true);
  const [productSellableUoms, setProductSellableUoms] = useState(null);
  // Full selected Product record (not just id/label) — /api/products search
  // results already carry availability_badge/availability_date/
  // availability_badge_color (productController.js's withAvailability), so
  // this is the only extra state needed to show the same pill here that
  // Products/UoM Retail Prices/Product UoM already show.
  const [selectedProduct, setSelectedProduct] = useState(null);

  const [showReport, setShowReport] = useState(false);

  // Same Scope-select convention as PriceForm's Customer Scope:
  // 'all' | 'group' | 'specific', driving which field (if any) shows below it.
  const [customerScopeType, setCustomerScopeType] = useState('all');

  const [customerId, setCustomerId] = useState('');
  const [customerSearch, setCustomerSearch] = useState('');
  const [customerResults, setCustomerResults] = useState([]);
  const [showCustomerResults, setShowCustomerResults] = useState(false);
  const skipNextCustomerSearch = useRef(true);

  const [customerGroups, setCustomerGroups] = useState([]);
  const [customerPriceGroupId, setCustomerPriceGroupId] = useState('');

  const [uomId, setUomId] = useState('');
  const [qty, setQty] = useState('1');
  const [asOfDate, setAsOfDate] = useState(() => todayInTenant());

  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  // The view picked on the toggle for this result; null = still on the one
  // it opened on (see openingGstView below). Reset by each new lookup.
  const [gstView, setGstView] = useState(null);

  const anchorName = customerGroups.find((g) => g.is_system_default)?.name || 'Retail';

  useEffect(() => {
    axios.get('/api/customer-price-groups')
      .then((res) => setCustomerGroups((res.data.data || []).filter((g) => g.is_active)))
      .catch(() => setCustomerGroups([]));
  }, []);

  // Same live search-as-you-type pattern as the Product list's SKU/name search.
  useEffect(() => {
    if (skipNextProductSearch.current) {
      skipNextProductSearch.current = false;
      return;
    }
    if (!productSearch.trim()) {
      setProductResults([]);
      setShowProductResults(false);
      return;
    }
    const timer = setTimeout(() => {
      axios.get('/api/products', { params: { search: productSearch, status: 'active', limit: 10 } })
        .then((res) => {
          setProductResults(res.data.data || []);
          setShowProductResults(true);
        })
        .catch(() => setProductResults([]));
    }, 250);
    return () => clearTimeout(timer);
  }, [productSearch]);

  // A specific Product's own sellable UoMs (can_sell = true) — the UoM
  // field only ever offers UoMs this Product is actually sold in, same
  // rule PriceForm's UoM field already enforces once a specific
  // Product is picked.
  useEffect(() => {
    if (!productId) {
      setProductSellableUoms(null);
      return;
    }
    axios.get(`/api/products/${productId}/uoms`)
      .then((res) => setProductSellableUoms((res.data.data?.uoms || []).filter((pu) => pu.can_sell)))
      .catch(() => setProductSellableUoms([]));
  }, [productId]);

  const handleSelectProduct = (p) => {
    skipNextProductSearch.current = true;
    setProductId(String(p.id));
    setProductSearch(productLabel(p));
    setSelectedProduct(p);
    setShowProductResults(false);
    setUomId('');
  };

  // Clearing the Product resets the whole page back to its just-loaded
  // state, not just the Product/UoM fields — a stale result card (or a
  // Customer/Group pick left over from the previous lookup) referencing a
  // product that's no longer selected is confusing, not useful.
  const handleClearProduct = () => {
    skipNextProductSearch.current = true;
    skipNextCustomerSearch.current = true;
    setProductId('');
    setProductSearch('');
    setSelectedProduct(null);
    setShowProductResults(false);
    setProductSellableUoms(null);
    setUomId('');
    setQty('1');
    setAsOfDate(todayInTenant());
    setCustomerScopeType('all');
    setCustomerId('');
    setCustomerSearch('');
    setShowCustomerResults(false);
    setCustomerPriceGroupId('');
    setResult(null);
    setShowReport(false);
  };

  // Same live search-as-you-type pattern as the Product picker.
  useEffect(() => {
    if (skipNextCustomerSearch.current) {
      skipNextCustomerSearch.current = false;
      return;
    }
    if (!customerSearch.trim()) {
      setCustomerResults([]);
      setShowCustomerResults(false);
      return;
    }
    const timer = setTimeout(() => {
      axios.get('/api/customers', { params: { scope: 'quotable', search: customerSearch, status: 'active', limit: 10 } })
        .then((res) => {
          setCustomerResults(res.data.data || []);
          setShowCustomerResults(true);
        })
        .catch(() => setCustomerResults([]));
    }, 250);
    return () => clearTimeout(timer);
  }, [customerSearch]);

  const handleSelectCustomer = (c) => {
    skipNextCustomerSearch.current = true;
    setCustomerId(String(c.id));
    setCustomerSearch(customerLabel(c));
    setShowCustomerResults(false);
  };

  const handleClearCustomer = () => {
    skipNextCustomerSearch.current = true;
    setCustomerId('');
    setCustomerSearch('');
    setShowCustomerResults(false);
  };

  // Same pattern as PriceForm's Customer Scope select: switching
  // scope clears whatever the previous scope had picked, so a stale
  // Customer/Group selection from a prior scope can never leak into a
  // lookup run under a different scope.
  const handleSetCustomerScope = (scope) => {
    setCustomerScopeType(scope);
    skipNextCustomerSearch.current = true;
    setCustomerId('');
    setCustomerSearch('');
    setShowCustomerResults(false);
    setCustomerPriceGroupId('');
  };

  const handleLookup = async () => {
    if (!productId || !uomId) return;
    setLoading(true);
    setResult(null);
    setGstView(null);
    try {
      const params = {
        product_id: productId,
        uom_id: uomId,
        qty: qty || undefined,
        as_of_date: asOfDate || undefined,
      };
      if (customerScopeType === 'specific' && customerId) params.customer_id = customerId;
      if (customerScopeType === 'group' && customerPriceGroupId) params.customer_price_group_id = customerPriceGroupId;
      const res = await axios.get('/api/price-lookup', { params });
      setResult(res.data.data);
      dismissError();
    } catch (err) {
      // A missing Retail Price is a configuration gap the lookup itself
      // surfaced, not a failed request — shown inline in the results area
      // (same spot a normal result would go) instead of the error toast.
      if (err.response?.data?.error === 'RETAIL_PRICE_MISSING') {
        const d = err.response.data;
        setResult({ error: 'RETAIL_PRICE_MISSING', sku: d.sku, uomCode: d.uomCode, anchorGroupName: d.anchorGroupName, productId: d.productId });
        dismissError();
      } else {
        showError(err, 'Price Lookup failed');
      }
    }
    setLoading(false);
  };

  // Min/Max/Step Order Quantity for the selected UoM — same
  // order_qty_constraint fields PricePricingPage.js already
  // applies to its own Qty inputs, sourced here from the same
  // /api/products/:id/uoms row the UoM dropdown itself is built from.
  const selectedProductUom = (productSellableUoms || []).find((pu) => String(pu.uom_id) === String(uomId));
  const qtyMin = selectedProductUom ? selectedProductUom.min_order_qty : 1;
  const qtyStep = selectedProductUom ? selectedProductUom.qty_step : 1;
  const qtyMax = selectedProductUom?.max_order_qty ?? undefined;

  // Selected UoM's own name (label only — the id already lives in uomId) —
  // needed for the report's "Filtered by" line since that's read later,
  // detached from the live dropdown it came from.
  const uomLabel = selectedProductUom ? selectedProductUom.name : '';

  const customerScopeLabel = customerScopeType === 'specific'
    ? (customerSearch || 'Specific Customer')
    : customerScopeType === 'group'
      ? (customerGroups.find((g) => String(g.id) === String(customerPriceGroupId)) ? groupLabel(customerGroups.find((g) => String(g.id) === String(customerPriceGroupId))) : 'Customer Price Group')
      : 'All Customers';

  // Ex GST / Inc GST — view only, prices are resolved from stored figures
  // either way. Opens on the scope's Pricing Basis (a Customer's Tax
  // Handling, or the one a Customer Price Group's Tier(s) agree on —
  // priceResolver.js's taxHandling), else on the viewer's last pick.
  const gstApplicable = !!result?.rows?.some((r) => r.gstApplicable);
  const taxHandling = result?.taxHandling || null;
  const openingGstView = taxHandling ? (taxHandling.pricingBasis === 'retail' ? 'inc' : 'ex') : readStoredGstView();
  const viewInc = gstApplicable && (gstView || openingGstView) === 'inc';
  const handleSetGstView = (view) => {
    setGstView(view);
    storeGstView(view);
  };

  const reportFilterSummary = `Product = ${productSearch || '(none)'}, Customer Scope = ${customerScopeLabel}, UoM = ${uomLabel || '(none)'}, Qty = ${formatNumber(qty, { maxDecimals: 4 })}, As Of = ${formatDate(asOfDate)}, Prices = ${viewInc ? 'Inc GST' : 'Ex GST'}`;

  return (
    <div style={{ width: '100%', maxWidth: 960, margin: '32px auto 48px', background: '#f6f8fa', borderRadius: 12, boxShadow: '0 2px 12px rgba(0,0,0,0.07)', padding: 24 }}>
      <h2 style={{ margin: '0 0 4px', fontWeight: 600, fontSize: 24 }}>Price Lookup</h2>
      <div style={{ fontSize: 13, color: '#888', marginBottom: 20 }}>
        Resolves the actual price — unit price and extended (qty × unit) price, {anchorName} Price plus the winning Price, ties broken — for one Product + UoM + Qty combo against a specific Customer or Customer Price Group.
      </div>

      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <div style={{ position: 'relative' }}>
          <label style={{ display: 'block', fontSize: 12, color: '#555', marginBottom: 4 }}>Product<RequiredMark /></label>
          <input
            placeholder="Search name or SKU..."
            value={productSearch}
            onChange={(e) => { setProductId(''); setProductSearch(e.target.value); setSelectedProduct(null); }}
            onFocus={() => { if (productResults.length) setShowProductResults(true); }}
            onBlur={() => setTimeout(() => setShowProductResults(false), 150)}
            style={{ padding: '6px 10px', borderRadius: 6, border: '1px solid #bfc8d6', fontSize: 14, width: 260 }}
          />
          {productId && (
            <button type="button" onClick={handleClearProduct} title="Clear product" style={{ marginLeft: 6, padding: '4px 8px', fontSize: 12 }}>×</button>
          )}
          {showProductResults && productResults.length > 0 && (
            <ul style={{ position: 'absolute', zIndex: 10, top: '100%', left: 0, width: 260, margin: 0, padding: 4, listStyle: 'none', background: '#fff', border: '1px solid #bfc8d6', borderRadius: 6, boxShadow: '0 2px 8px rgba(0,0,0,0.12)', maxHeight: 220, overflowY: 'auto' }}>
              {productResults.map((p) => (
                <li
                  key={p.id}
                  onMouseDown={() => handleSelectProduct(p)}
                  style={{ padding: '6px 8px', cursor: 'pointer', fontSize: 13, borderRadius: 4 }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = '#e3eaf3'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                >
                  {productLabel(p)}
                </li>
              ))}
            </ul>
          )}
        </div>

        {selectedProduct && (selectedProduct.availability_badge || selectedProduct.availability_date) && (
          <div>
            {/* A plain date (no active badge) is when the Product became available. */}
            {!selectedProduct.availability_badge && (
              <label style={{ display: 'block', fontSize: 12, color: '#555', marginBottom: 4 }}>Since</label>
            )}
            <div style={{ padding: '6px 0', border: '1px solid transparent', fontSize: 14, display: 'flex', alignItems: 'center' }}>
              <AvailabilityPill badge={selectedProduct.availability_badge} date={selectedProduct.availability_date} color={selectedProduct.availability_badge_color} showDate />
            </div>
          </div>
        )}

        <div>
          <label style={{ display: 'block', fontSize: 12, color: '#555', marginBottom: 4 }}>Customer Scope<RequiredMark /></label>
          <select className="styled-input"
            value={customerScopeType}
            onChange={(e) => handleSetCustomerScope(e.target.value)}
            style={{ padding: '6px 10px', borderRadius: 6, border: '1px solid #bfc8d6', fontSize: 14 }}
          >
            <option value="all">All</option>
            <option value="group">Customer Price Group</option>
            <option value="specific">Specific Customer</option>
          </select>
        </div>
        {customerScopeType === 'group' && (
          <div>
            <label style={{ display: 'block', fontSize: 12, color: '#555', marginBottom: 4 }}>Customer Price Group<RequiredMark /></label>
            <select className="styled-input"
              value={customerPriceGroupId}
              onChange={(e) => setCustomerPriceGroupId(e.target.value)}
              style={{ padding: '6px 10px', borderRadius: 6, border: '1px solid #bfc8d6', fontSize: 14, width: 220 }}
            >
              <option value="">Select a group…</option>
              {customerGroups.map((g) => (
                <option key={g.id} value={g.id}>{'   '.repeat(g.depth || 0)}{g.name}</option>
              ))}
            </select>
            <Link
              to="/sales-customer-price-groups"
              style={{ marginLeft: 10, color: '#1976d2', fontSize: 13, textDecoration: 'underline' }}
            >
              Manage groups
            </Link>
          </div>
        )}
        {customerScopeType === 'specific' && (
          <div style={{ position: 'relative' }}>
            <label style={{ display: 'block', fontSize: 12, color: '#555', marginBottom: 4 }}>Customer<RequiredMark /></label>
            <input
              placeholder="Search code or name..."
              value={customerSearch}
              onChange={(e) => { setCustomerId(''); setCustomerSearch(e.target.value); }}
              onFocus={() => { if (customerResults.length) setShowCustomerResults(true); }}
              onBlur={() => setTimeout(() => setShowCustomerResults(false), 150)}
              style={{ padding: '6px 10px', borderRadius: 6, border: '1px solid #bfc8d6', fontSize: 14, width: 300 }}
            />
            {customerId && (
              <button type="button" onClick={handleClearCustomer} title="Clear customer" style={{ marginLeft: 6, padding: '4px 8px', fontSize: 12 }}>×</button>
            )}
            {showCustomerResults && customerResults.length > 0 && (
              <ul style={{ position: 'absolute', zIndex: 10, top: '100%', left: 0, width: 300, margin: 0, padding: 4, listStyle: 'none', background: '#fff', border: '1px solid #bfc8d6', borderRadius: 6, boxShadow: '0 2px 8px rgba(0,0,0,0.12)', maxHeight: 220, overflowY: 'auto' }}>
                {customerResults.map((c) => (
                  <li
                    key={c.id}
                    onMouseDown={() => handleSelectCustomer(c)}
                    style={{ padding: '6px 8px', cursor: 'pointer', fontSize: 13, borderRadius: 4 }}
                    onMouseEnter={(e) => { e.currentTarget.style.background = '#e3eaf3'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                  >
                    {customerLabel(c)}
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        <div>
          <label style={{ display: 'block', fontSize: 12, color: '#555', marginBottom: 4 }}>UoM<RequiredMark /></label>
          <select className="styled-input"
            value={uomId}
            onChange={(e) => {
              const newUomId = e.target.value;
              setUomId(newUomId);
              const pu = (productSellableUoms || []).find((p) => String(p.uom_id) === String(newUomId));
              setQty(pu ? String(pu.min_order_qty) : '1');
            }}
            style={{ padding: '6px 10px', borderRadius: 6, border: '1px solid #bfc8d6', fontSize: 14, minWidth: 140 }}
          >
            <option value="">Select a UoM...</option>
            {(productSellableUoms || []).map((pu) => (
              <option key={pu.uom_id} value={pu.uom_id}>{pu.name}</option>
            ))}
          </select>
        </div>

        <div>
          <label style={{ display: 'block', fontSize: 12, color: '#555', marginBottom: 4 }}>Qty<RequiredMark /></label>
          <NumberInput
            stepper
            maxDecimals={4}
            min={qtyMin}
            max={qtyMax}
            step={qtyStep}
            value={qty}
            onChange={(e) => setQty(e.target.value)}
            style={{ padding: '6px 10px', borderRadius: 6, border: '1px solid #bfc8d6', fontSize: 14, width: 80 }}
          />
        </div>

        <div>
          <label style={{ display: 'block', fontSize: 12, color: '#555', marginBottom: 4 }}>As of</label>
          <DateInput value={asOfDate} onChange={(e) => setAsOfDate(e.target.value)} style={{ padding: '6px 10px', borderRadius: 6, border: '1px solid #bfc8d6', fontSize: 14 }} />
        </div>

        <button
          onClick={handleLookup}
          disabled={!productId || !uomId || loading}
          style={{ padding: '8px 20px', background: !productId || !uomId || loading ? '#9fb3c8' : '#1976d2', color: '#fff', border: 'none', borderRadius: 6, fontWeight: 500, cursor: !productId || !uomId || loading ? 'not-allowed' : 'pointer' }}
        >
          {loading ? 'Looking up...' : 'Look Up'}
        </button>

        {/* Single-record contextual report (pat-architecture-reports-ui-
            standard.md) — one "Print" trigger, no dropdown needed since
            there's only ever the one report for this screen (same shape as
            ProductTable.js's single-product "Print" row action). Disabled
            until a lookup has actually run, same gating Look Up itself uses. */}
        <button
          type="button"
          onClick={() => setShowReport(true)}
          disabled={!result || result.error}
          title={!result || result.error ? 'Run a lookup first' : undefined}
          style={reportButtonStyle(!result || result.error)}
        >
          Print
        </button>
      </div>

      {result && (
        <div style={{ marginTop: 24 }}>
          {result.error === 'RETAIL_PRICE_MISSING' ? (
            <RetailPriceMissingBanner sku={result.sku} uomCode={result.uomCode} anchorGroupName={result.anchorGroupName} productId={result.productId} />
          ) : (
            <>
              <SkipNotes result={result} customerScopeType={customerScopeType} />
              {result.rows.map((row) => (
                <PriceLookupResultRow
                  key={row.uomId}
                  row={row}
                  qty={result.qty}
                  productSearch={productSearch}
                  customerSearch={customerSearch}
                  customerGroups={customerGroups}
                  viewInc={viewInc}
                  taxHandling={taxHandling}
                  titleAction={gstApplicable ? <SegmentedToggle options={GST_VIEWS} value={viewInc ? 'inc' : 'ex'} onChange={handleSetGstView} /> : null}
                />
              ))}
            </>
          )}
        </div>
      )}

      {showReport && (
        <ReportOverlay onClose={() => setShowReport(false)}>
          <PriceLookupReportView
            result={result}
            product={selectedProduct}
            customerScopeType={customerScopeType}
            customerScopeLabel={customerScopeLabel}
            productSearch={productSearch}
            customerSearch={customerSearch}
            customerGroups={customerGroups}
            filterSummary={reportFilterSummary}
            viewInc={viewInc}
            onClose={() => setShowReport(false)}
          />
        </ReportOverlay>
      )}
    </div>
  );
}

export default PriceLookupPage;
